import { CustomTag } from "sipli-fleet-core/components/CustomSharedTag";
import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { App, Button, Dropdown } from "antd";
import type { TableProps } from "antd";
import {
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import CrossIcon from "sipli-fleet-core/assets/icons/cross-icon.svg";
import DeleteIcon from "sipli-fleet-core/assets/icons/delete-icon.svg";
import EditIcon from "sipli-fleet-core/assets/icons/edit-icon.svg";
import MenuIcon from "sipli-fleet-core/assets/icons/menu-icon.svg";
import SearchIcon from "sipli-fleet-core/assets/icons/search-icon.svg";

import {
  CustomButton,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";

import {
  useMutationDeleteParameter,
  useMutationDeleteParameterGroup,
  useMutationUpdateParameterGroup,
  useMutationUpdateParameterStatus,
  useQueryGetParameterColumns,
  useQueryGetParameterGroupColumns,
  useQueryGetParameterGroupsList,
  useQueryGetParametersList,
} from "src/queries/administrationSetup.queries";
import type {
  ParameterGroupRecord,
  ParameterRecord,
  SetupListParams,
} from "src/services/administrationSetupService";
import { hasAdministrationPermission } from "src/util/administrationPermission";
import ParameterGroupsAddEdit from "./ParameterGroupsAddEdit";
import ParameterGroupsDelete from "./ParameterGroupsDelete";
import { getColumnOptions, getEntityName, getRecords, getTotalCount, isActive } from "../setupUtils";

type ParameterSetupKind = "group" | "parameter";
type ParameterSetupRecord = ParameterGroupRecord | ParameterRecord;

type SetupSorting = {
  sortBy: string;
  sort: "ASC" | "DESC";
};

const CrossIconComponent = CrossIcon as unknown as ComponentType<{
  className?: string;
}>;

const DeleteIconComponent = DeleteIcon as unknown as ComponentType<{
  className?: string;
}>;

const EditIconComponent = EditIcon as unknown as ComponentType<{
  className?: string;
}>;

const MenuIconComponent = MenuIcon as unknown as ComponentType<{
  className?: string;
}>;

const SearchIconComponent = SearchIcon as unknown as ComponentType<{
  className?: string;
}>;

const { CustomText } = CustomTypography;

const DEFAULT_PAGINATION = {
  page: 0,
  size: 10,
};

const DEFAULT_SORTING: SetupSorting = {
  sortBy: "ID",
  sort: "DESC",
};

const groupDataIndexToColumnId: Record<string, string> = {
  name: "NAME",
  code: "CODE",
  status: "STATUS",
};

const parameterDataIndexToColumnId: Record<string, string> = {
  parameterGroup: "GROUP_ID",
  propertyNameComm: "GROUP_NAME",
  propertyNameInternal: "PROPERTY_NAME_INTERNAL",
  packetCode: "PACKET_CODE",
  status: "STATUS",
};

const getDescription = (value: unknown) => {
  const text = typeof value === "string" ? value.trim() : "";

  return text || "--";
};

const DescriptionCell = ({ value }: { value: unknown }) => (
  <CustomTooltip title={getDescription(value)}>
    <span className="block max-w-[320px] truncate">{getDescription(value)}</span>
  </CustomTooltip>
);

const StatusBadge = ({ active }: { active?: boolean | number }) => {
  const { t } = useTranslation();

  return (
    <CustomTag
      className={
        isActive(active)
          ? "bg-green-50 text-green-600"
          : "bg-gray-50 text-gray-400"
      }
    >
      {isActive(active) ? t("active") : t("inactive")}
    </CustomTag>
  );
};

const TechnicalDetailsCell = ({ record }: { record: ParameterRecord }) => {
  const { t, i18n } = useTranslation();

  return (
    <div className="min-w-[220px] space-y-1 text-xs leading-[18px] text-gray9">
      <div>
        <span className="font-semibold">{t("unit")}:</span> {record.units || "--"}
      </div>
      <div>
        <span className="font-semibold">{t("min_value")}:</span> {formatNumber(record.minValue, i18n.language)}
      </div>
      <div>
        <span className="font-semibold">{t("max_value")}:</span> {formatNumber(record.maxValue, i18n.language)}
      </div>
      <div>
        <span className="font-semibold">{t("multiplier")}:</span> {formatNumber(record.multiplier, i18n.language)}
      </div>
      <div>
        <span className="font-semibold">{t("bytes")}:</span> {formatNumber(record.bytes, i18n.language)}
      </div>
      <div>
        <span className="font-semibold">{t("type")}:</span> {record.type || "--"}
      </div>
    </div>
  );
};

const PossibleValuesCount = ({ record }: { record: ParameterRecord }) => {
  const { i18n } = useTranslation();
  const count = record.possibleValues?.length ?? 0;

  return (
    <span
      className={
        count
          ? "inline-flex h-[30px] min-w-[30px] items-center justify-center rounded bg-green-600 px-2 text-sm font-semibold text-white"
          : "inline-flex h-[30px] min-w-[30px] items-center justify-center rounded bg-gray-400 px-2 text-sm font-semibold text-white"
      }
    >
      {formatNumber(count, i18n.language)}
    </span>
  );
};

const getRecordSearchText = (record: ParameterSetupRecord, kind: ParameterSetupKind) => {
  const group = record as ParameterGroupRecord;
  const parts: string[] = [
    group.name ?? "",
    group.code ?? "",
    isActive(record.active) ? "active" : "inactive",
  ];

  if (kind === "parameter") {
    const parameter = record as ParameterRecord;

    parts.push(
      getEntityName(parameter.parameterGroup),
      parameter.packetCode ?? "",
      parameter.propertyNameComm ?? "",
      parameter.propertyNameInternal ?? "",
      parameter.description ?? "",
      parameter.bytes ?? "",
      parameter.type ?? "",
      parameter.minValue ?? "",
      parameter.maxValue ?? "",
      parameter.multiplier ?? "",
      parameter.units ?? "",
      getEntityName(parameter.make),
      String(parameter.possibleValues?.length ?? 0)
    );
  }

  return parts.join(" ").toLowerCase();
};

const getSetupKind = (): ParameterSetupKind => "group";

const ParameterGroupsList = () => {
  const kind = getSetupKind();
  const { t } = useTranslation();
  const { message } = App.useApp();

  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<SetupSorting>(DEFAULT_SORTING);
  const [search, setSearch] = useState("");
  const [formRecord, setFormRecord] = useState<ParameterSetupRecord | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState<ParameterSetupRecord | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | number | null>(null);
  const [openActionId, setOpenActionId] = useState<string | number | null>(null);

  const canEdit = hasAdministrationPermission("edit");
  const canDelete = hasAdministrationPermission("delete");
  const isGroup = kind === "group";

  const queryParams: SetupListParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      sort: sorting.sort,
      sortBy: sorting.sortBy,
    }),
    [pagination.page, pagination.size, sorting.sort, sorting.sortBy]
  );

  const groupQuery = useQueryGetParameterGroupsList(queryParams, {
    enabled: isGroup,
  });

  const parameterQuery = useQueryGetParametersList(queryParams, {
    enabled: !isGroup,
  });

  const groupColumnsQuery = useQueryGetParameterGroupColumns(isGroup);
  const parameterColumnsQuery = useQueryGetParameterColumns(!isGroup);
  const updateGroupMutation = useMutationUpdateParameterGroup();
  const updateParameterStatusMutation = useMutationUpdateParameterStatus();
  const deleteGroupMutation = useMutationDeleteParameterGroup();
  const deleteParameterMutation = useMutationDeleteParameter();

  const activeQuery = isGroup ? groupQuery : parameterQuery;
  const response = activeQuery.data?.data?.response;
  const records = getRecords(
    response,
    isGroup ? "parameterGroups" : "properties"
  ) as ParameterSetupRecord[];

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return records;

    return records.filter((record) => getRecordSearchText(record, kind).includes(normalizedSearch));
  }, [kind, records, search]);

  const totalCount = search.trim()
    ? filteredRecords.length
    : getTotalCount(response, records.length);

  const columnOptions = useMemo(() => {
    const payload = isGroup ? groupColumnsQuery.data?.data : parameterColumnsQuery.data?.data;
    const lookupOptions = getColumnOptions(payload);

    if (lookupOptions.length) return lookupOptions;

    return Object.entries(isGroup ? groupDataIndexToColumnId : parameterDataIndexToColumnId)
      .filter(([, value]) => value !== "STATUS")
      .map(([key, value]) => ({
        label: t(key),
        value,
      }));
  }, [groupColumnsQuery.data?.data, isGroup, parameterColumnsQuery.data?.data, t]);

  const selectedSortByValue = useMemo(() => {
    const hasSelectedOption = columnOptions.some(
      (option) => String(option.value) === sorting.sortBy
    );

    return hasSelectedOption ? sorting.sortBy : undefined;
  }, [columnOptions, sorting.sortBy]);

  void selectedSortByValue;

  const handleTableChange: TableProps<ParameterSetupRecord>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action === "filter") return;

    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const dataIndex = normalizedSorter?.field || normalizedSorter?.columnKey;
    const columnId = dataIndex
      ? (isGroup ? groupDataIndexToColumnId : parameterDataIndexToColumnId)[String(dataIndex)]
      : undefined;

    if (!normalizedSorter?.order || !columnId) {
      setSorting(DEFAULT_SORTING);
    } else {
      setSorting({
        sortBy: columnId,
        sort: normalizedSorter.order === "ascend" ? "ASC" : "DESC",
      });
    }

    setPagination((previous) => ({ ...previous, page: 0 }));
  };

  const openCreateForm = useCallback(() => {
    setFormRecord(null);
    setIsFormOpen(true);
  }, []);

  const openEditForm = useCallback((record: ParameterSetupRecord) => {
    setFormRecord(record);
    setIsFormOpen(true);
  }, []);

  const handleStatusChange = useCallback(
    async (record: ParameterSetupRecord) => {
      setStatusUpdatingId(record.id);

      try {
        if (isGroup) {
          await updateGroupMutation.mutateAsync({
            id: record.id,
            payload: {
              active: !isActive(record.active),
            },
          });
        } else {
          await updateParameterStatusMutation.mutateAsync({
            id: record.id,
            active: !isActive(record.active),
          });
        }

        setOpenActionId(null);
        message.success(t("status_updated_successfully"));
      } catch {
        message.error(t("setup_status_update_failed"));
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [isGroup, message, t, updateGroupMutation, updateParameterStatusMutation]
  );

  const handleDeleteConfirm = async () => {
    if (!deleteRecord) return;

    try {
      if (isGroup) {
        await deleteGroupMutation.mutateAsync(deleteRecord.id);
      } else {
        await deleteParameterMutation.mutateAsync(deleteRecord.id);
      }

      message.success(t(isGroup ? "parameter_group_deleted" : "parameter_deleted_successfully"));
      setDeleteRecord(null);
    } catch (error: unknown) {
      const details =
        error instanceof Error
          ? (error as { response?: { data?: { details?: string } } }).response?.data?.details
          : undefined;

      message.error(
        isGroup
          ? t("cannot_delete_item")
          : `${t("error_while_deleting")}${details ? `: ${details}` : ""}`
      );
    }
  };

  const renderActions = useCallback(
    (record: ParameterSetupRecord): ReactNode => {
      const isCurrentStatusLoading = statusUpdatingId === record.id;
      const disabledActionClass =
        "!cursor-not-allowed !opacity-90 hover:!bg-transparent active:!bg-transparent focus:!bg-transparent !bg-transparent";

      const handleStatusClick = async (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();

        await handleStatusChange(record);
      };

      return (
        <div className="flex items-center justify-center gap-2 users-actions">
          <CustomTooltip title={t("edit")}>
            <CustomButton
              type="icon"
              buttonSize="small"
              aria-label={t("edit")}
              disabled={!canEdit}
              className={!canEdit ? disabledActionClass : "focus:!bg-transparent"}
              icon={
                <EditIconComponent
                  className={`w-[16px] h-[16px] ${!canEdit ? "opacity-30 grayscale" : ""}`}
                />
              }
              onClick={(event) => {
                event?.stopPropagation?.();
                openEditForm(record);
              }}
            />
          </CustomTooltip>

          <CustomTooltip title={t("delete")}>
            <CustomButton
              type="icon"
              buttonSize="small"
              aria-label={t("delete")}
              disabled={!canDelete}
              className={!canDelete ? disabledActionClass : "focus:!bg-transparent"}
              icon={
                <DeleteIconComponent
                  className={`w-[15px] h-[16px] ${!canDelete ? "opacity-30 grayscale" : ""}`}
                />
              }
              onClick={(event) => {
                event?.stopPropagation?.();
                setDeleteRecord(record);
              }}
            />
          </CustomTooltip>

          <Dropdown
            disabled={!canEdit}
            trigger={["click"]}
            open={openActionId === record.id}
            onOpenChange={(open) => setOpenActionId(open ? record.id : null)}
            dropdownRender={() => (
              <div
                className="min-w-[180px] overflow-hidden rounded-sm bg-white shadow-[0_6px_16px_rgba(0,0,0,0.16)]"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <Button
                  type="text"
                  loading={isCurrentStatusLoading}
                  className="!flex !h-[36px] !w-full !cursor-pointer !items-center !justify-start !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!bg-white active:!bg-white focus:!outline-none active:!outline-none"
                  onClick={handleStatusClick}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  {isActive(record.active) ? t("inactive") : t("active")}
                </Button>
              </div>
            )}
            placement="bottomRight"
            overlayClassName="user-action-dropdown"
          >
            <CustomTooltip title={t("more_action")}>
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("more_actions")}
                disabled={!canEdit}
                icon={<MenuIconComponent className="w-[3px] h-[16px]" />}
                className="ml-2 !bg-transparent active:!bg-transparent focus:!bg-transparent"
                onClick={(event) => {
                  event?.stopPropagation?.();
                }}
                onMouseDown={(event) => {
                  event?.preventDefault?.();
                  event?.stopPropagation?.();
                }}
              />
            </CustomTooltip>
          </Dropdown>
        </div>
      );
    },
    [canDelete, canEdit, handleStatusChange, openActionId, openEditForm, statusUpdatingId, t]
  );

  const columns: TableProps<ParameterSetupRecord>["columns"] = useMemo(() => {
    if (isGroup) {
      return [
        {
          title: t("name"),
          dataIndex: "name",
          key: "name",
          sorter: true,
          render: (value: string) => (
            <CustomText className="!text-sm !font-semibold !text-black">{value || "--"}</CustomText>
          ),
        },
        {
          title: t("code"),
          dataIndex: "code",
          key: "code",
          sorter: true,
          render: (value: string) => value || "--",
        },
        {
          title: t("status"),
          dataIndex: "status",
          key: "status",
          sorter: true,
          render: (_value, record) => <StatusBadge active={record.active} />,
        },
        {
          title: t("actions"),
          key: "actions",
          align: "center",
          width: 120,
          render: (_value, record) => renderActions(record),
        },
      ];
    }

    return [
      {
        title: t("group"),
        dataIndex: "parameterGroup",
        key: "parameterGroup",
        sorter: true,
        render: (value) => getEntityName(value) || "--",
      },
      {
        title: t("name"),
        dataIndex: "propertyNameComm",
        key: "propertyNameComm",
        sorter: true,
        render: (value: string) => (
          <CustomText className="!text-sm !font-semibold !text-black">{value || "--"}</CustomText>
        ),
      },
      {
        title: t("property_name_internal"),
        dataIndex: "propertyNameInternal",
        key: "propertyNameInternal",
        sorter: true,
        render: (value: string) => value || "--",
      },
      {
        title: t("packet_code"),
        dataIndex: "packetCode",
        key: "packetCode",
        sorter: true,
        render: (value: string) => value || "--",
      },
      {
        title: t("technical_details"),
        key: "technicaldetails",
        render: (_value, record) => <TechnicalDetailsCell record={record as ParameterRecord} />,
      },
      {
        title: t("possible_values"),
        dataIndex: "possibleValues",
        key: "possibleValues",
        align: "center",
        render: (_value, record) => <PossibleValuesCount record={record as ParameterRecord} />,
      },
      {
        title: t("description"),
        dataIndex: "description",
        key: "description",
        render: (value) => <DescriptionCell value={value} />,
      },
      {
        title: t("status"),
        dataIndex: "status",
        key: "status",
        sorter: true,
        render: (_value, record) => <StatusBadge active={record.active} />,
      },
      {
        title: t("actions"),
        key: "actions",
        align: "center",
        width: 120,
        render: (_value, record) => renderActions(record),
      },
    ];
  }, [isGroup, renderActions, t]);

  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey={isGroup ? "parameter_group" : "parameters"}
        buttonKey="create"
        onClick={openCreateForm}
        showBtn={canEdit}
        buttonProps={{
          className: " !text-gray2 !w-[94px] !h-[40px]",
        }}
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <div className="flex flex-wrap items-center gap-3 px-[18px] -mt-2 justify-between">
          <CustomSharedInput
            allowClear={{
              clearIcon: (
                <CrossIconComponent className="mr-0.5 users-search-clear-icon w-[8.5px] h-[8.5px]" />
              ),
            }}
            prefix={<SearchIconComponent className="users-search-icon w-[14px] h-[14px]" />}
            placeholder={t("search")}
            value={search}
            className="global-filter-search-class !rounded-[4px]"
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination((previous) => ({ ...previous, page: 0 }));
            }}
          />

          {/* <div className="flex items-center gap-2 mt-6">
            <CustomSelect
              className="min-w-[170px]"
              placeholder={t("sort_by_column")}
              value={selectedSortByValue}
              options={columnOptions}
              allowClear
              onChange={(value) =>
                setSorting((previous) => ({
                  ...previous,
                  sortBy: value ? String(value) : DEFAULT_SORTING.sortBy,
                }))
              }
              showSearch
              optionFilterProp="label"
            />

            <CustomButton
              type="secondary"
              icon={
                sorting.sort === "ASC" ? (
                  <ArrowUp2 size={16} color="#4B5563" />
                ) : (
                  <ArrowDown2 size={16} color="#4B5563" />
                )
              }
              label={t(sorting.sort === "ASC" ? "ascending" : "descending")}
              onClick={() =>
                setSorting((previous) => ({
                  ...previous,
                  sort: previous.sort === "ASC" ? "DESC" : "ASC",
                }))
              }
            />
          </div> */}
        </div>

        <CustomTable<ParameterSetupRecord>
          data={filteredRecords}
          columns={columns}
          rowKey={(record) => String(record.id)}
          isLoading={activeQuery.isLoading}
          pageSize={pagination.size}
          currentPage={pagination.page}
          totalCount={totalCount}
          handlePagination={(page) => setPagination((previous) => ({ ...previous, page }))}
          handlePageSizeChange={(size) =>
            setPagination({
              page: 0,
              size: Number(size),
            })
          }
          showSearchInput={false}
          showCustomizeColumns={false}
          showDownload={false}
          onChange={handleTableChange}
          showTopHorizontalScrollbar
          locale={{ emptyText: t("no_data") }}
        />
      </div>

      <ParameterGroupsAddEdit
        open={isFormOpen}
        record={formRecord}
        onClose={() => setIsFormOpen(false)}
      />

      <ParameterGroupsDelete
        open={Boolean(deleteRecord)}
        isLoading={isGroup ? deleteGroupMutation.isPending : deleteParameterMutation.isPending}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDeleteConfirm}
      />
    </section>
  );
};

export default ParameterGroupsList;
